import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import PriceForm from './priceForm';
import { isLoadingByKey } from '../../shared/controls/methods/getLoadingState';
import apiServices from '../../services/service';
import { toast } from 'react-toastify';
import { getActiveOwners, getGridDataByModuleKey } from '../../redux/selector';
import { useDispatch, useSelector } from 'react-redux';
import { initValuePrice } from '../../shared/initials';
import { fetchDataAndUpdateStore, singleRowAddUpdateInStore } from '../../redux/actions';
import { validationSchemaPrice } from './priceValidationSchema';
import Loader from '../../shared/component/Loader';

const CL_ICONS = {
  chevL: <path d="M15 18l-6-6 6-6" />,
  check: <path d="M20 6 9 17l-5-5" />,
};
const CLIc = ({ name, size = 16, style }) => (
  <svg viewBox="0 0 24 24" width={size} height={size} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" style={style}>
    {CL_ICONS[name] || null}
  </svg>
);

export const AddPriceList = () => {
  const stateData = useSelector((state) => state);
  let storeLoading = isLoadingByKey(
    stateData,
    'revenueTypeList',
    'leadSourceList',
    'industryList',
    'timeZone'
  );
  const containerStyle = useMemo(() => ({ width: '100%', height: '100%' }), []);
  const location = useLocation();
  const [formData, setFormData] = useState(initValuePrice);
  const [errormsg, setErrorMsg] = useState({});
  const [selectedOptions, setSelectedOptions] = useState({});
  const [loading, setLoading] = useState(storeLoading);
  const navigate = useNavigate();
  const gridData = useSelector(getGridDataByModuleKey('priceSheetList'));
  const dispatch = useDispatch();
  const [imagefile, setImageFile] = useState(null);
  const [formEdited, setFormEdited] = useState(false);

  useEffect(() => {
    let updatedFormData = {
      ...formData,
      type: location.pathname.includes('createPrice') ? 'add' : 'edit',
    };
    if (location.pathname.includes('editPrice') && location.state) {
      updatedFormData = {
        ...location.state,
        type: 'edit',
      };
    }
    setFormData(updatedFormData);
  }, [location]);

  const handleCloseAddForm = () => {
    if (formData.from === 'view') {
      navigate(
        `/products/priceSheet/viewPriceSheet?id=${formData.id}&pageNo=${gridData?.inputPayload?.pageNumber}`
      );
    } else {
      navigate('/products/priceSheet');
    }
  };

  const handleUploadImage = async (imagefile) => {
    setImageFile(imagefile);
    setFormEdited(true);
  };

  const handleChange = (selectedOption, name) => {
    setSelectedOptions((prevState) => ({
      ...prevState,
      [name]: selectedOption,
    }));

    handleLookupDropDownChange(selectedOption.value, selectedOption, name);
  };

  const handleLookupDropDownChange = (selectedId, selectedOption, name) => {
    const selectedValue = selectedOption.label;
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));

    if (name === 'revenueTypeId') {
      setFormData({
        ...formData,
        revenueTypeId: selectedId,
        revenueType: selectedValue,
      });

      setFormEdited(true);
    } else if (name === 'leadSourceId') {
      setFormData({
        ...formData,
        leadSourceId: selectedId,
        leadSource: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'industryId') {
      setFormData({
        ...formData,
        industryId: selectedId,
        industry: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'ownerId') {
      setFormData({
        ...formData,
        ownerId: selectedId,
        owner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'timeZoneId') {
      setFormData({
        ...formData,
        timeZoneId: selectedId,
        timeZone: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'priceSheetOwnerId') {
      setFormData({
        ...formData,
        priceSheetOwnerId: selectedId,
        priceSheetOwner: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'priceModelId') {
      setFormData({
        ...formData,
        priceModelId: selectedId,
        priceModel: selectedValue,
      });
      setFormEdited(true);
    } else if (name === 'currencyPriceId') {
      setFormData({
        ...formData,
        currencyPriceId: selectedId,
        currencyPrice: selectedValue,
      });
      setFormEdited(true);
    }
  };

  const handleChangeForm = (e) => {
    const { name, value } = e.target;
    let convertedValue = value;

    // For numeric-only fields
    if (['quantityInDemand', 'quantityOrdered', 'quantityInStock'].includes(name)) {
      convertedValue = value.replace(/[^0-9]/g, '');
    }

    // For unitPrice
    if (['unitPrice'].includes(name)) {
      if (value === '') {
        convertedValue = null; // Send null instead of empty string
      } else {
        let sanitized = value.replace(/[^0-9.]/g, '');

        const parts = sanitized.split('.');
        if (parts.length > 2) {
          sanitized = parts[0] + '.' + parts[1];
        }

        if (parts.length === 2) {
          parts[1] = parts[1].slice(0, 2);
          sanitized = parts[0] + '.' + parts[1];
        }

        const parsed = parseFloat(sanitized);
        convertedValue = !isNaN(parsed) && /^\d+(\.\d{1,2})?$/.test(sanitized) ? parsed : sanitized;
      }
    }

    // Clear the error for this field
    setErrorMsg((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Update form data
    setFormData((prev) => ({
      ...prev,
      [name]: convertedValue,
    }));

    setFormEdited(true);
  };

  const handleChangeCheckbox = (e) => {
    const { name, checked } = e.target; // Use 'checked' for true/false value
    setErrorMsg((prevErrorMsg) => ({
      ...prevErrorMsg,
      [name]: undefined,
    }));
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked, // Set the checkbox state as true/false
    }));
    setFormEdited(true);
  };

  const handleSubmit = async (formData) => {
    setLoading(true);
    try {
      const sanitizedData = {
        ...formData,
      };

      await validationSchemaPrice.validate(sanitizedData, {
        abortEarly: false,
      });
      handleFormSubmit(sanitizedData);
    } catch (error) {
      setLoading(false);
      const fieldErrors = {};
      error.inner.forEach((err) => {
        fieldErrors[err.path] = err.message;
      });
      setErrorMsg(fieldErrors);
    }
  };

  const handleFormSubmit = (formData) => {
    if (formData.type === 'add') {
      apiServices
        .createPrice(formData)
        .then((response) => {
          if (response.status === 200) {
            const id = response.data;
            updateList('Price created successfully!',id);
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    } else {
      //   editUploadImage(formData.id);
      apiServices
        .updatePrice(formData)
        .then((response) => {
          if (response.status === 200) {
            updateList('Price updated successfully!');
            setLoading(false);
            handleCloseAddForm();
          } else {
            setLoading(false);
            const errorMessages = [];
            for (const key in response.errors) {
              if (response.errors.hasOwnProperty(key)) {
                const messages = response.errors[key];
                errorMessages.push(...messages);
              }
            }
            errorMessages.forEach((errorMessage) => {
              toast.error(errorMessage, { position: 'top-right' });
            });
          }
        })

        .catch((error) => {
          console.error('Error:', error, 'DataFormat:', formData);
          toast.error('An unexpected error occurred.', {
            position: 'top-right',
          });
          setLoading(false);
        });
    }
  };

  const updateList = async (msg, id) => {
    setLoading(false);

    toast.success(msg, {
      position: 'top-right',
    });

    const columnName = 'prodcutName';
    const orderType = 'desc';
    const data = {
      pageSize: parseInt(process.env.REACT_APP_DEFAULT_PAGESIZE),
      pageNumber: 1,
      columnName,
      orderType,
      filterJson: null,
    };

    try {
      // ✅ Await the dispatch before navigation
      // await dispatch(
      //   fetchDataAndUpdateStore({ key: "priceSheetList", inputData: data })
      // );
      dispatch(
        singleRowAddUpdateInStore({
          moduleName: 'PriceSheet',
          moduleKey: 'priceSheetList',
          id: formData.type === 'add' ? id : formData.id,
          rowIndex: formData.rowIndex,
        })
      );
      if (formData.from === 'view') {
        navigate(
          `/products/PriceSheet/viewPriceSheet?id=${formData.id}`
        );
      } else {
        navigate('/products/PriceSheet');
      }
    } catch (error) {
      toast.error('Something went wrong while updating the list.', {
        position: 'top-right',
      });
    }
  };

  const isEdit = formData?.type === 'edit';
  const canSave = !!(formData?.name && String(formData.name).trim()) && !!formData?.startDate && !!formData?.endDate;

  return (
    <div className="gos-cl gos-quoteform">
      {loading && <Loader />}
      {/* sticky header */}
      <div className="gos-cl-header">
        <button type="button" className="gos-cl-back" onClick={handleCloseAddForm} title="Back to Price Sheets">
          <CLIc name="chevL" size={18} />
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="gos-cl-htitle">{isEdit ? 'Edit Price Sheet' : 'Create Price Sheet'}</div>
          <div className="gos-cl-hsub">
            Fields marked <span className="gos-cl-reqdot">*</span> are required
          </div>
        </div>
        <button type="button" className="gos-btn gos-btn-ghost" onClick={handleCloseAddForm}>Cancel</button>
        <button
          type="button"
          className="gos-btn gos-btn-primary"
          onClick={() => handleSubmit(formData)}
          disabled={loading || (isEdit && !formEdited) || !canSave}
          style={{ opacity: canSave && !(isEdit && !formEdited) ? 1 : 0.55 }}
        >
          {loading ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              Saving…
            </>
          ) : (
            <>
              <CLIc name="check" size={16} /> Save Price Sheet
            </>
          )}
        </button>
      </div>

      <div className="gos-cl-scroll">
        <PriceForm
          formData={formData}
          handleChangeForm={handleChangeForm}
          handleChangeCheckbox={handleChangeCheckbox}
          handleChange={handleChange}
          errormsg={errormsg}
        />
      </div>
    </div>
  );
};

export default AddPriceList;
